import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MutableRefObject,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import {
  shortOrderRef,
  UPLOAD_POLICIES,
  UPLOAD_PURPOSE_MARKETPLACE_PARTNER_PREFLIGHT,
  validateUploadMeta,
} from "@nexus/contracts";
import {
  Badge,
  Button,
  Icon,
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  Skeleton,
  Tabs,
  TabsList,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import { toast } from "sonner";

import { useAuth } from "@/features/auth/hooks/useAuth";
import { downloadPartnerOrderScriptAsset } from "@/features/marketplace/api/marketplace.api";
import { runPartnerPreflightUploadFlow } from "@/features/marketplace/api/partner-preflight-upload.api";
import { OrderScriptVideoActivitySections } from "@/features/marketplace/components/OrderScriptVideoActivitySections";
import { OrderScriptRowActions } from "@/features/marketplace/components/OrderScriptRowActions";
import { ClampedTitle } from "@/features/marketplace/components/orders-list/ClampedTitle";
import { CopyableId } from "@/features/marketplace/components/orders-list/CopyableId";
import { DeadlineChip } from "@/features/marketplace/components/orders-list/DeadlineChip";
import { OrderTypeBadge } from "@/features/marketplace/components/orders-list/OrderTypeBadge";
import { StatusPill } from "@/features/marketplace/components/orders-list/StatusMenu";
import { VideoStatusSummary } from "@/features/marketplace/components/VideoStatusSummary";
import { usesVideoStatusChips } from "@/features/marketplace/lib/line-status-display";
import { DetailCommentComposer } from "@/features/marketplace/components/orders-list/DetailCommentComposer";
import { ScriptReviewAction } from "@/features/marketplace/components/ScriptReviewAction";
import { VendorBriefSection } from "@/features/marketplace/components/orders-list/VendorBriefSection";
import { getMarketplacePartnerPreflightUploadConstraints } from "@/features/marketplace/lib/marketplace-partner-preflight-upload-constraints";
import { localTodayYmd } from "@/features/marketplace/lib/order-deadline";
import {
  resolveOrderLineType,
  toOrderLineTypeFilter,
} from "@/features/marketplace/lib/order-line-kind";
import { resolvePartnerPreflightContentType } from "@/features/marketplace/lib/resolve-partner-preflight-content-type";
import { describeOrderScript } from "@/features/marketplace/lib/order-script-display";
import { resolveRevisionDeliverableId } from "@/features/marketplace/lib/revision-deliverable";
import {
  clampPanelWidth,
  loadPanelWidth,
  persistPanelWidth,
} from "@/features/marketplace/lib/orders-detail-panel";
import { OrderPersonaBriefSection } from "@/features/marketplace/components/orders-list/OrderPersonaBriefSection";
import { ClientMark } from "@/features/marketplace/partner/components/ClientMark";
import { PartnerPublicScriptLink } from "@/features/marketplace/partner/components/PartnerPublicScriptLink";
import { PartnerQueueOverview } from "@/features/marketplace/partner/components/PartnerQueueOverview";
import { PartnerScriptProduction } from "@/features/marketplace/partner/components/PartnerScriptProduction";
import { PartnerQueueList } from "@/features/marketplace/partner/components/PartnerQueueList";
import { PartnerPreflightSuccessDialog } from "@/features/marketplace/partner/components/PartnerPreflightSuccessDialog";
import { PartnerVideoConfirmDialog } from "@/features/marketplace/partner/components/PartnerVideoConfirmDialog";
import {
  PartnerPreflightUploadProgress,
  type PartnerPreflightSubmitPhase,
} from "@/features/marketplace/partner/components/PartnerPreflightUploadProgress";
import { PartnerLineVideos } from "@/features/marketplace/partner/components/PartnerLineVideos";
import { OrderScriptThumbnails } from "@/features/marketplace/components/OrderScriptThumbnails";
import { isPartnerPreflightNotifyRetry } from "@/features/marketplace/partner/lib/partner-preflight-retry";
import {
  canAddPartnerVideo,
  canChangePartnerVideos,
  partnerDefaultReplaceVideo,
  partnerEmptyVideoSlots,
  partnerLineVideos,
  partnerWorkspaceShowsUploadTab,
  partnerVideoSelectionKey,
  partnerVideoSlotEmpty,
  resolvePartnerPreflightIntent,
  reviewIdAfterPartnerSubmit,
} from "@/features/marketplace/partner/lib/partner-videos";
import {
  describePartnerScriptProduction,
  hasPartnerScriptProduction,
} from "@/features/marketplace/partner/lib/partner-script-production";
import {
  usePartnerWorkspaceTab,
  type PartnerWorkspaceTab,
} from "@/features/marketplace/partner/lib/partner-workspace-tab";
import {
  buildPartnerWorkQueue,
  countPartnerOverdue,
  DEFAULT_PARTNER_QUEUE_FILTERS,
  listPartnerClients,
  PARTNER_QUEUE_TABS,
  partnerOrdersFilterForQueueTab,
  partnerQueueFiltersForDeepLink,
  type PartnerQueueFilters,
  type PartnerQueueTab,
  type PartnerWorkItem,
} from "@/features/marketplace/partner/lib/partner-work-queue";
import { partnerQueueViewState } from "@/features/marketplace/partner/lib/partner-queue-view-state";
import type {
  MarketplaceFulfillmentStatus,
  MarketplaceOrder,
  PartnerOrder,
  OrderLineVideo,
  OrderScriptRow,
  PartnerPreflightIntent,
} from "@/features/marketplace/types/marketplace.types";
import {
  UploadDropzone,
  type UploadDropzoneHandle,
} from "@/features/preflight/components/UploadDropzone/UploadDropzone";
import { formatRelativeOrDate } from "@/features/opportunities/lib/format-relative-time";
import { getUserFacingApiMessage } from "@/lib/api-client";
import {
  useAddPartnerOrderScriptNote,
  usePartnerOrderDetail,
  usePartnerOrders,
  usePartnerOrderScriptPreflightComments,
  usePartnerVoiceoverCheck,
  useAddPartnerOrderScriptVideoSlot,
  useRetryPartnerPreflightReview,
  useUploadPartnerPreflight,
} from "@/lib/queries";

export const PARTNER_QUEUE_PAGE_SIZE = 50;

const PARTNER_QUEUE_TAB_LABELS = {
  "needs-action": "Needs my action",
  active: "Active",
  "awaiting-review": "Waiting for feedback",
  done: "Done",
  archive: "Archive",
} as const satisfies Record<PartnerQueueTab, string>;

const PARTNER_VIDEO_POLICY =
  UPLOAD_POLICIES[UPLOAD_PURPOSE_MARKETPLACE_PARTNER_PREFLIGHT];
const PARTNER_PREFLIGHT_UPLOAD =
  getMarketplacePartnerPreflightUploadConstraints();

interface SelectedScript {
  orderId: string;
  orderScriptId: string;
  videoKey?: string | null;
}

interface PartnerPreflightSuccessState {
  scriptReviewId: string;
  scriptTitle: string;
  clientName: string;
  submissionStatus: MarketplaceFulfillmentStatus;
}

type PartnerPreflightRowPhase = PartnerPreflightSubmitPhase | "idle";

export function isPartnerUploadCancellablePhase(
  phase: PartnerPreflightRowPhase,
): boolean {
  return (
    phase === "preparing" || phase === "uploading" || phase === "confirming"
  );
}

export type { PartnerWorkspaceTab } from "@/features/marketplace/partner/lib/partner-workspace-tab";

function HeaderIconTooltip({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

export function PartnerScriptWorkspaceHeader({
  order,
  script,
  title,
  showPublicLink = true,
  activeTab,
  onTabChange,
  showUploadTab = false,
  activityCount,
  scriptIndex,
  scriptCount = 1,
  onPreviousScript,
  onNextScript,
}: {
  order: Pick<PartnerOrder, "etaDate" | "id" | "tenant" | "vendor">;
  script: Pick<
    OrderScriptRow,
    | "briefId"
    | "customAssetKind"
    | "expectedDeliveryDate"
    | "fulfillmentStatus"
    | "preflightUrl"
    | "scriptId"
    | "scriptReviewId"
    | "videos"
    | "videoCount"
  >;
  title: string;
  showPublicLink?: boolean;
  activeTab?: PartnerWorkspaceTab;
  onTabChange?: (tab: PartnerWorkspaceTab) => void;
  showUploadTab?: boolean;
  activityCount?: number;
  scriptIndex?: number;
  scriptCount?: number;
  onPreviousScript?: () => void;
  onNextScript?: () => void;
}) {
  const { t } = useTranslation();
  const dueDate = script.expectedDeliveryDate ?? order.etaDate;
  const orderRef = shortOrderRef(order.id) ?? order.id;
  const resolvedIndex = scriptIndex ?? 0;
  const hasScriptNav = scriptCount > 1;
  const tabOptions = [
    {
      value: "script",
      label: t("marketplace.partner.workspace.script", "Script"),
    },
    ...(showUploadTab
      ? [
          {
            value: "upload",
            label: t("marketplace.partner.workspace.uploadTab", "Upload"),
          },
        ]
      : []),
    {
      value: "activity",
      label: t("marketplace.partner.workspace.activity", "Activity"),
      badge: activityCount && activityCount > 0 ? activityCount : undefined,
    },
  ];

  return (
    <SheetHeader
      className={cn(
        "gap-3 border-b border-border bg-background px-6 py-0 pt-4",
        activeTab && onTabChange ? "pb-0" : "pb-4",
      )}
    >
      <SheetDescription className="sr-only">
        {t("marketplace.orders.list.drawer.description", { title })}
      </SheetDescription>
      <div className="flex items-start justify-between gap-2">
        <div className="text-caption tracking-label flex min-w-0 items-center gap-2 text-muted-foreground">
          <ClientMark
            name={order.tenant.name}
            logoUrl={order.tenant.logoUrl}
            brandColor={order.tenant.brandColor}
            size="sm"
          />
          <span className="truncate uppercase">{order.tenant.name}</span>
          <span aria-hidden>·</span>
          <CopyableId
            value={order.id}
            displayValue={orderRef}
            label={t("marketplace.orders.list.panel.orderLabel")}
            copyLabel={t("marketplace.partner.copyOrderRef")}
            copiedMessage={t("marketplace.partner.orderRefCopied")}
            truncate={false}
          />
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          {hasScriptNav ? (
            <>
              <span className="text-caption mr-1 tabular-nums text-muted-foreground">
                {t("marketplace.orders.detail.scriptPosition", {
                  index: resolvedIndex + 1,
                  total: scriptCount,
                })}
              </span>
              <HeaderIconTooltip
                label={t("marketplace.orders.list.panel.previousScript")}
              >
                <span className="inline-flex">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t(
                      "marketplace.orders.list.panel.previousScript",
                    )}
                    className="text-muted-foreground"
                    disabled={resolvedIndex <= 0}
                    onClick={onPreviousScript}
                  >
                    <Icon name="chevron-left" size="sm" color="inherit" />
                  </Button>
                </span>
              </HeaderIconTooltip>
              <HeaderIconTooltip
                label={t("marketplace.orders.list.panel.nextScript")}
              >
                <span className="inline-flex">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t("marketplace.orders.list.panel.nextScript")}
                    className="text-muted-foreground"
                    disabled={resolvedIndex >= scriptCount - 1}
                    onClick={onNextScript}
                  >
                    <Icon name="chevron-right" size="sm" color="inherit" />
                  </Button>
                </span>
              </HeaderIconTooltip>
            </>
          ) : null}
          <HeaderIconTooltip label={t("marketplace.orders.list.panel.close")}>
            <span className="inline-flex">
              <SheetClose asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t("marketplace.orders.list.panel.close")}
                  className="text-muted-foreground"
                >
                  <Icon name="x" size="sm" color="inherit" />
                </Button>
              </SheetClose>
            </span>
          </HeaderIconTooltip>
        </div>
      </div>

      <ClampedTitle className="text-drawer-title">{title}</ClampedTitle>
      {showPublicLink && script.scriptId ? (
        <PartnerPublicScriptLink scriptId={script.scriptId} />
      ) : null}

      <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          {usesVideoStatusChips(script) ? (
            <VideoStatusSummary script={script} viewer="partner" />
          ) : (
            <StatusPill status={script.fulfillmentStatus} viewer="partner" />
          )}
          <OrderTypeBadge type={resolveOrderLineType(script)} />
          <DeadlineChip
            ymd={dueDate}
            todayYmd={localTodayYmd()}
            workingWeekdays={order.vendor.workingWeekdays}
          />
        </div>
        {script.scriptReviewId || script.preflightUrl ? (
          <ScriptReviewAction
            scriptReviewId={script.scriptReviewId}
            preflightUrl={script.preflightUrl}
            label={t(
              "marketplace.partner.actions.viewVideoReview",
              "View video review",
            )}
            ariaLabel={t("marketplace.partner.actions.viewVideoReviewAria", {
              title,
              defaultValue:
                "View video review for {{title}} (opens in a new tab)",
            })}
            variant="outline"
            className="ml-auto"
          />
        ) : null}
      </div>

      {activeTab && onTabChange ? (
        <Tabs
          value={activeTab}
          onValueChange={(value) => {
            if (value === "script" || value === "activity") {
              onTabChange(value);
              return;
            }
            if (value === "upload" && showUploadTab) {
              onTabChange(value);
            }
          }}
          className="-mb-px gap-0"
        >
          <TabsList
            variant="line"
            aria-label={t(
              "marketplace.partner.workspace.tabs",
              "Workspace sections",
            )}
            className="h-auto min-h-11 w-full justify-start border-b-0"
            options={tabOptions}
          />
        </Tabs>
      ) : null}
    </SheetHeader>
  );
}

export function PartnerProductionQueuePage({
  initialOrderId,
  initialOrderScriptId,
}: {
  initialOrderId?: string;
  initialOrderScriptId?: string;
} = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const activeTenantId = useAuth((state) => state.activeTenantId);
  const searchRef = useRef<HTMLInputElement>(null);
  const trimmedInitialOrderId = initialOrderId?.trim() || undefined;
  const trimmedInitialOrderScriptId = initialOrderScriptId?.trim() || undefined;
  const deepLinkKey = trimmedInitialOrderId
    ? `${trimmedInitialOrderId}:${trimmedInitialOrderScriptId ?? ""}`
    : null;
  const [handledDeepLinkKey, setHandledDeepLinkKey] = useState<string | null>(
    null,
  );
  if (deepLinkKey === null && handledDeepLinkKey !== null) {
    setHandledDeepLinkKey(null);
  }
  const [query, setQuery] = useState<PartnerQueueFilters>(
    DEFAULT_PARTNER_QUEUE_FILTERS,
  );
  const [selected, setSelected] = useState<SelectedScript | null>(null);
  const [expandedScriptVideoIds, setExpandedScriptVideoIds] = useState<
    ReadonlySet<string>
  >(() => new Set());
  const [offset, setOffset] = useState(0);
  const [successDialog, setSuccessDialog] =
    useState<PartnerPreflightSuccessState | null>(null);
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [panelWidth, setPanelWidth] = useState(loadPanelWidth);
  const isDeepLinkPending =
    deepLinkKey !== null && handledDeepLinkKey !== deepLinkKey;
  const serverFilter = isDeepLinkPending
    ? "all"
    : partnerOrdersFilterForQueueTab(query.tab);
  const serverLineType = isDeepLinkPending
    ? undefined
    : toOrderLineTypeFilter(query.lineType);

  const { data, isLoading, isError, isFetching, dataUpdatedAt, refetch } =
    usePartnerOrders(activeTenantId, {
      filter: serverFilter,
      lineType: serverLineType,
      limit: PARTNER_QUEUE_PAGE_SIZE,
      offset,
      orderId: trimmedInitialOrderId,
    });
  const { data: overviewPage } = usePartnerOrders(activeTenantId, {
    filter: "all",
    limit: PARTNER_QUEUE_PAGE_SIZE,
    offset: 0,
  });
  const queueView = partnerQueueViewState({
    hasData: data !== undefined,
    isPending: isLoading,
    isError,
  });
  const orders = data?.orders ?? [];
  const total = data?.total ?? 0;
  const canLoadMore = offset + PARTNER_QUEUE_PAGE_SIZE < total;
  const { data: selectedOrderDetail } = usePartnerOrderDetail(
    activeTenantId,
    selected?.orderId,
  );

  const unnamedScriptLabel = t(
    "marketplace.orders.detail.unnamedScript",
    "Unnamed script",
  );
  const clients = useMemo(() => listPartnerClients(orders), [orders]);
  const groups = useMemo(
    () => buildPartnerWorkQueue(orders, query, unnamedScriptLabel),
    [orders, query, unnamedScriptLabel],
  );
  const tabGroups = useMemo(
    () =>
      buildPartnerWorkQueue(
        orders,
        { ...DEFAULT_PARTNER_QUEUE_FILTERS, tab: query.tab },
        unnamedScriptLabel,
      ),
    [orders, query.tab, unnamedScriptLabel],
  );
  const overdueCount = useMemo(
    () => countPartnerOverdue(orders, query, unnamedScriptLabel),
    [orders, query, unnamedScriptLabel],
  );
  const selectedOrder = selected
    ? (orders.find((order) => order.id === selected.orderId) ?? null)
    : null;
  const selectedScript =
    selectedOrder && selected
      ? ((selectedOrderDetail ?? selectedOrder).scripts.find(
          (script) => script.id === selected.orderScriptId,
        ) ?? null)
      : null;
  const openScriptIds = useCallback(
    (
      orderId: string,
      orderScriptId: string,
      videoKey?: string | null,
    ) => {
      setSelected({ orderId, orderScriptId, videoKey: videoKey ?? null });
      void navigate({
        to: "/marketplace/partner",
        search: { orderId, orderScriptId },
        replace: true,
      });
    },
    [navigate],
  );
  const [pendingUploadTab, setPendingUploadTab] = useState(false);
  const consumePendingUploadTab = useCallback(() => setPendingUploadTab(false), []);
  const openScript = useCallback(
    (item: PartnerWorkItem) => {
      setPendingUploadTab(false);
      openScriptIds(item.order.id, item.script.id, null);
    },
    [openScriptIds],
  );
  const openScriptForUpload = useCallback(
    (item: PartnerWorkItem, video?: OrderLineVideo) => {
      setPendingUploadTab(true);
      openScriptIds(
        item.order.id,
        item.script.id,
        video ? partnerVideoSelectionKey(video) : null,
      );
    },
    [openScriptIds],
  );
  const toggleScriptVideosExpanded = useCallback((scriptId: string) => {
    setExpandedScriptVideoIds((current) => {
      const next = new Set(current);
      if (next.has(scriptId)) next.delete(scriptId);
      else next.add(scriptId);
      return next;
    });
  }, []);
  const closeScript = useCallback(() => {
    setSelected(null);
    void navigate({
      to: "/marketplace/partner",
      search: {},
      replace: true,
    });
  }, [navigate]);
  const handleQueryChange = useCallback(
    (next: PartnerQueueFilters) => {
      if (next.lineType !== query.lineType) setOffset(0);
      setQuery(next);
    },
    [query.lineType],
  );

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    setOffset(0);
  }, [activeTenantId, serverFilter, serverLineType]);

  useEffect(() => {
    if (!isDeepLinkPending || orders.length === 0 || !trimmedInitialOrderId) {
      return;
    }
    const order = orders.find(
      (candidate) => candidate.id === trimmedInitialOrderId,
    );
    const script =
      order?.scripts.find(
        (candidate) => candidate.id === trimmedInitialOrderScriptId,
      ) ?? order?.scripts[0];
    setHandledDeepLinkKey(deepLinkKey);
    if (order && script) {
      setSelected({ orderId: order.id, orderScriptId: script.id });
      setQuery((current) => partnerQueueFiltersForDeepLink(current, script));
    }
  }, [
    deepLinkKey,
    isDeepLinkPending,
    orders,
    trimmedInitialOrderId,
    trimmedInitialOrderScriptId,
  ]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        isPartnerQueueInteractiveTarget(event.target)
      ) {
        return;
      }
      if (event.key === "/") {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <main className="mx-auto flex w-full max-w-[1400px] flex-col gap-5 px-4 py-5 sm:px-6 sm:py-7">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl font-medium tracking-tight text-foreground">
            {t("marketplace.partner.title", "Production orders")}
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            {t(
              "marketplace.partner.subtitle",
              "Upload your finalized creative for Adge and client review, and track revision requests in one place.",
            )}
          </p>
        </div>
        <div
          className="flex min-h-6 items-center gap-2 text-xs text-muted-foreground"
          aria-live="polite"
        >
          {!isOnline ? (
            <Badge variant="status-error" dot>
              {t("marketplace.partner.offline", "Offline — showing saved work")}
            </Badge>
          ) : (
            <>
              {isFetching ? (
                <Icon
                  name="loader"
                  size="xs"
                  className="animate-spin"
                  aria-hidden
                />
              ) : null}
              {dataUpdatedAt > 0 && queueView !== "error" ? (
                <span>
                  {t("marketplace.partner.updated", "Updated {{time}}", {
                    time: formatRelativeOrDate(
                      new Date(dataUpdatedAt).toISOString(),
                      Date.now(),
                    ),
                  })}
                </span>
              ) : null}
            </>
          )}
        </div>
      </header>

      {queueView === "ready" || queueView === "stale" ? (
        <PartnerQueueOverview orders={overviewPage?.orders ?? orders} />
      ) : null}

      <nav
        className="flex min-h-11 gap-5 overflow-x-auto border-b border-border"
        aria-label={t(
          "marketplace.partner.queueViews",
          "Production queue views",
        )}
      >
        {PARTNER_QUEUE_TABS.map((option) => (
          <button
            key={option}
            type="button"
            role="tab"
            aria-selected={query.tab === option}
            className={cn(
              "relative min-h-11 shrink-0 cursor-pointer px-0.5 text-sm text-muted-foreground transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
              query.tab === option &&
                "font-medium text-foreground after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-primary",
            )}
            onClick={() => {
              setQuery((current) => ({ ...current, tab: option }));
            }}
          >
            {t(
              `marketplace.partner.queueTabs.${option}`,
              PARTNER_QUEUE_TAB_LABELS[option],
            )}
          </button>
        ))}
      </nav>

      {queueView === "loading" ? (
        <QueueSkeleton />
      ) : queueView === "error" ? (
        <QueueState
          icon="alert-circle"
          title={t(
            "marketplace.partner.error.title",
            "Production work couldn't load",
          )}
          body={t(
            "marketplace.partner.error.body",
            "Check your connection and try again.",
          )}
          action={
            <Button
              type="button"
              variant="outline"
              onClick={() => void refetch()}
            >
              {t("marketplace.partner.error.retry", "Try again")}
            </Button>
          }
        />
      ) : (
        <div className="flex flex-col gap-3">
          {queueView === "stale" ? (
            <div
              className="flex flex-col gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
              role="status"
            >
              <p className="text-sm text-muted-foreground">
                {t(
                  "marketplace.partner.error.stale",
                  "Couldn't refresh the queue. Showing the last loaded work.",
                )}
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isFetching}
                onClick={() => void refetch()}
              >
                {t("marketplace.partner.error.retry", "Try again")}
              </Button>
            </div>
          ) : null}
          <PartnerQueueList
            groups={groups}
            query={query}
            clients={clients}
            overdueCount={overdueCount}
            totalCount={total}
            selectedScriptId={selected?.orderScriptId ?? null}
            selectedOrderId={selected?.orderId ?? null}
            selectedVideoKey={selected?.videoKey ?? null}
            expandedScriptVideoIds={expandedScriptVideoIds}
            searchInputRef={searchRef}
            onQueryChange={handleQueryChange}
            onToggleScriptVideosExpanded={toggleScriptVideosExpanded}
            onOpen={openScript}
            onOpenForUpload={openScriptForUpload}
          />
          {total > 0 ? (
            <p className="text-xs text-muted-foreground">
              {t("marketplace.partner.queueTotal", "{{count}} orders", {
                count: total,
              })}
            </p>
          ) : null}
          {canLoadMore ? (
            <div className="flex justify-center">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isFetching}
                onClick={() =>
                  setOffset((current) => current + PARTNER_QUEUE_PAGE_SIZE)
                }
              >
                {t("marketplace.orders.list.loadMore", "Load more")}
              </Button>
            </div>
          ) : null}
        </div>
      )}

      {selectedOrder && selectedScript ? (
        <PartnerScriptWorkspace
          order={selectedOrder}
          script={selectedScript}
          width={panelWidth}
          pendingUploadTab={pendingUploadTab}
          initialReplaceVideoKey={selected?.videoKey ?? null}
          onPendingUploadTabConsumed={consumePendingUploadTab}
          onClose={closeScript}
          onWidthChange={setPanelWidth}
          onSelectScript={(orderScriptId) => {
            setPendingUploadTab(false);
            openScriptIds(selectedOrder.id, orderScriptId, null);
          }}
          onUploadSuccess={setSuccessDialog}
        />
      ) : null}

      {successDialog ? (
        <PartnerPreflightSuccessDialog
          open
          onOpenChange={(open) => {
            if (!open) setSuccessDialog(null);
          }}
          scriptReviewId={successDialog.scriptReviewId}
          scriptTitle={successDialog.scriptTitle}
          clientName={successDialog.clientName}
          submissionStatus={successDialog.submissionStatus}
        />
      ) : null}
    </main>
  );
}

function PartnerScriptWorkspace({
  order,
  script,
  width,
  pendingUploadTab,
  initialReplaceVideoKey,
  onPendingUploadTabConsumed,
  onClose,
  onWidthChange,
  onSelectScript,
  onUploadSuccess,
}: {
  order: PartnerOrder;
  script: OrderScriptRow;
  width: number;
  pendingUploadTab: boolean;
  initialReplaceVideoKey: string | null;
  onPendingUploadTabConsumed: () => void;
  onClose: () => void;
  onWidthChange: (width: number) => void;
  onSelectScript: (orderScriptId: string) => void;
  onUploadSuccess: (state: PartnerPreflightSuccessState) => void;
}) {
  const { t } = useTranslation();
  const activeTenantId = useAuth((state) => state.activeTenantId);
  const widthRef = useRef(width);
  widthRef.current = width;
  const uploadAbortRef = useRef<AbortController | null>(null);
  const closedRef = useRef(false);
  const display = describeOrderScript(
    script,
    t("marketplace.orders.detail.unnamedScript", "Unnamed script"),
  );
  const personaBrief = display.personaBrief;
  const production = describePartnerScriptProduction(script);
  const activityDeliverableId = resolveRevisionDeliverableId(
    script,
    initialReplaceVideoKey,
  );
  const { data: activity = [] } = usePartnerOrderScriptPreflightComments(
    activeTenantId,
    order.id,
    script.id,
    true,
    activityDeliverableId,
  );
  const addNote = useAddPartnerOrderScriptNote();
  const showPreflightSubmit = partnerWorkspaceShowsUploadTab(script);
  const scriptIndex = order.scripts.findIndex((row) => row.id === script.id);

  const [activeTab, setActiveTab] = usePartnerWorkspaceTab(
    script.id,
    pendingUploadTab,
    showPreflightSubmit,
    onPendingUploadTabConsumed,
  );

  const handleResizePointerDown = (
    event: ReactPointerEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
    const handle = event.currentTarget;
    handle.setPointerCapture(event.pointerId);
    const startX = event.clientX;
    const startWidth = widthRef.current;
    const onMove = (moveEvent: PointerEvent) => {
      onWidthChange(clampPanelWidth(startWidth + (startX - moveEvent.clientX)));
    };
    const onUp = () => {
      handle.removeEventListener("pointermove", onMove);
      persistPanelWidth(widthRef.current);
    };
    handle.addEventListener("pointermove", onMove);
    handle.addEventListener("pointerup", onUp, { once: true });
  };

  return (
    <Sheet
      open
      onOpenChange={(open) => {
        if (!open) {
          closedRef.current = true;
          uploadAbortRef.current?.abort();
          onClose();
        }
      }}
    >
      <SheetContent
        side="right"
        className="flex w-full flex-col p-0 max-w-[min(var(--orders-panel-width),100vw)]"
        style={{ "--orders-panel-width": `${width}px` } as CSSProperties}
        showClose={false}
      >
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={t("marketplace.orders.list.panel.resize")}
          tabIndex={-1}
          className="absolute inset-y-0 left-0 z-10 w-1.5 cursor-col-resize touch-none hover:bg-primary/20"
          onPointerDown={handleResizePointerDown}
          onKeyDown={(event) => {
            if (event.key === "ArrowLeft") {
              event.preventDefault();
              onWidthChange(clampPanelWidth(width + 16));
            }
            if (event.key === "ArrowRight") {
              event.preventDefault();
              onWidthChange(clampPanelWidth(width - 16));
            }
          }}
        />
        <PartnerScriptWorkspaceHeader
          order={order}
          script={script}
          title={display.title}
          showPublicLink={!display.uploadId && !display.briefOnly}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          showUploadTab={showPreflightSubmit}
          activityCount={activity.length}
          scriptIndex={scriptIndex >= 0 ? scriptIndex : 0}
          scriptCount={order.scripts.length}
          onPreviousScript={() => {
            const previous = order.scripts[scriptIndex - 1];
            if (previous) onSelectScript(previous.id);
          }}
          onNextScript={() => {
            const next = order.scripts[scriptIndex + 1];
            if (next) onSelectScript(next.id);
          }}
        />
        <SheetBody className="flex flex-col gap-6">
          {activeTab === "upload" && showPreflightSubmit ? (
            <PartnerUploadPanel
              order={order}
              script={script}
              title={display.title}
              initialReplaceVideoKey={initialReplaceVideoKey}
              abortRef={uploadAbortRef}
              onUploadSuccess={(state) => {
                if (closedRef.current) return;
                onUploadSuccess(state);
              }}
            />
          ) : activeTab === "script" ? (
            <>
              {showPreflightSubmit ? (
                <div
                  className="flex flex-col gap-3 rounded-md border border-border bg-muted/30 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                  role="region"
                  aria-labelledby="partner-upload-cta-title"
                >
                  <div className="min-w-0">
                    <h2
                      id="partner-upload-cta-title"
                      className="text-sm font-medium text-foreground"
                    >
                      {t(
                        "marketplace.partner.workspace.uploadCtaTitle",
                        "Production video",
                      )}
                    </h2>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {t(
                        "marketplace.partner.workspace.uploadCtaDescription",
                        "Upload your finished video, then submit it for ops review.",
                      )}
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    className="shrink-0"
                    onClick={() => setActiveTab("upload")}
                  >
                    {t(
                      "marketplace.partner.workspace.uploadCtaAction",
                      "Upload video",
                    )}
                  </Button>
                </div>
              ) : null}
              <VendorBriefSection
                snapshot={order.handoff}
                tenantId={order.tenantId}
                tenantVendorId={order.tenantVendorId}
                orgDefaults={null}
                viewer="partner"
                scripts={order.scripts}
              />

              <section
                className="flex flex-col gap-4"
                aria-labelledby="partner-script-content"
              >
                <h2
                  id="partner-script-content"
                  className="text-sm font-medium text-foreground"
                >
                  {t("marketplace.partner.workspace.script", "Script")}
                </h2>
                {!display.uploadId && !display.briefOnly ? (
                  <PartnerScriptProduction
                    script={script}
                    title={display.title}
                  />
                ) : null}
                {!hasPartnerScriptProduction(production) && display.hook ? (
                  <ScriptField
                    label={t("marketplace.partner.workspace.hook", "Hook")}
                    value={display.hook}
                  />
                ) : null}
                {!hasPartnerScriptProduction(production) && display.synopsis ? (
                  <ScriptField
                    label={t(
                      "marketplace.partner.workspace.synopsis",
                      "Synopsis",
                    )}
                    value={display.synopsis}
                  />
                ) : null}
                {!hasPartnerScriptProduction(production) &&
                display.fullScript ? (
                  <ScriptField
                    label={t(
                      "marketplace.partner.workspace.fullScript",
                      "Full script",
                    )}
                    value={display.fullScript}
                  />
                ) : null}
                {!display.hook &&
                !display.synopsis &&
                !display.fullScript &&
                !hasPartnerScriptProduction(production) ? (
                  <p className="text-sm text-muted-foreground">
                    {t(
                      "marketplace.partner.workspace.noScriptText",
                      "No script text was stored with this order.",
                    )}
                  </p>
                ) : null}
                {personaBrief ? (
                  <OrderPersonaBriefSection snapshot={personaBrief} />
                ) : null}
                <OrderScriptRowActions
                  briefUrl={display.briefUrl}
                  uploadId={display.uploadId}
                  downloadLabel={
                    script.customAssetKind === "BRIEF" || display.briefOnly
                      ? "brief"
                      : "script"
                  }
                  onDownloadAsset={
                    display.uploadId && activeTenantId
                      ? () =>
                          downloadPartnerOrderScriptAsset(
                            activeTenantId,
                            order.id,
                            script.id,
                          )
                      : undefined
                  }
                  className="flex-row flex-wrap"
                />
              </section>
            </>
          ) : (
            <OrderScriptVideoActivitySections
              script={script}
              entries={activity}
              viewer="partner"
              showFilters
              collapseSystemRuns
            />
          )}
        </SheetBody>
        <DetailCommentComposer
          viewer="partner"
          pending={addNote.isPending}
          onSubmit={async (body) => {
            if (!activeTenantId) return;
            await addNote.mutateAsync({
              partnerTenantId: activeTenantId,
              orderId: order.id,
              orderScriptId: script.id,
              body,
              ...(activityDeliverableId
                ? { deliverableId: activityDeliverableId }
                : {}),
            });
          }}
        />
      </SheetContent>
    </Sheet>
  );
}

export function PartnerUploadPanel({
  order,
  script,
  title,
  abortRef,
  initialReplaceVideoKey,
  onUploadSuccess,
}: {
  order: PartnerOrder;
  script: OrderScriptRow;
  title: string;
  initialReplaceVideoKey: string | null;
  abortRef: MutableRefObject<AbortController | null>;
  onUploadSuccess: (state: PartnerPreflightSuccessState) => void;
}) {
  const { t } = useTranslation();
  const activeTenantId = useAuth((state) => state.activeTenantId);
  const uploadPreflight = useUploadPartnerPreflight();
  const retryPreflightReview = useRetryPartnerPreflightReview();
  const addVideoSlot = useAddPartnerOrderScriptVideoSlot();
  const voiceoverCheck = usePartnerVoiceoverCheck();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [pendingUploadId, setPendingUploadId] = useState<string | null>(null);
  const [fileRejection, setFileRejection] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadPhase, setUploadPhase] =
    useState<PartnerPreflightRowPhase>("idle");
  const [replaceVideo, setReplaceVideo] = useState<OrderLineVideo | null>(null);
  const [replaceDialogVideo, setReplaceDialogVideo] =
    useState<OrderLineVideo | null>(null);
  const [replaceScriptId, setReplaceScriptId] = useState(script.id);
  const [appliedInitialVideoKey, setAppliedInitialVideoKey] = useState<
    string | null
  >(null);
  if (replaceScriptId !== script.id) {
    setReplaceScriptId(script.id);
    setReplaceVideo(null);
    setAppliedInitialVideoKey(null);
  }
  const videosForInit = partnerLineVideos(script);
  if (
    initialReplaceVideoKey &&
    appliedInitialVideoKey !== initialReplaceVideoKey
  ) {
    const match = videosForInit.find(
      (video) => partnerVideoSelectionKey(video) === initialReplaceVideoKey,
    );
    setAppliedInitialVideoKey(initialReplaceVideoKey);
    setReplaceVideo(match ?? null);
  }
  const uploadDropzoneRef = useRef<UploadDropzoneHandle>(null);
  const videos = partnerLineVideos(script);
  const hasVideos = videos.length > 0;
  const effectiveReplaceVideo =
    replaceVideo ?? partnerDefaultReplaceVideo(script);
  const canSubmitWithVideos =
    !hasVideos ||
    resolvePartnerPreflightIntent(script, replaceVideo) !== undefined;
  const isSubmitting = uploading || uploadPreflight.isPending;
  const canRetryNotify = isPartnerPreflightNotifyRetry(script);
  const replaceDialogActive = replaceDialogVideo !== null;
  const dialogTargetsReplaceVideo =
    replaceDialogVideo !== null &&
    replaceVideo !== null &&
    partnerVideoSelectionKey(replaceVideo) ===
      partnerVideoSelectionKey(replaceDialogVideo);
  const dialogSelectedFile = replaceDialogActive && dialogTargetsReplaceVideo
    ? selectedFile
    : null;
  const dialogSubmitLabel =
    replaceDialogVideo && partnerVideoSlotEmpty(replaceDialogVideo)
      ? t("marketplace.partner.videos.addSubmit", "Add video")
      : t("marketplace.partner.videos.replaceSubmit", "Replace video");

  const handleReplaceVideo = (video: OrderLineVideo) => {
    setReplaceDialogVideo(video);
  };

  const handleReplaceDialogFiles = (files: FileList | File[]) => {
    if (!replaceDialogVideo) return;
    setReplaceVideo(replaceDialogVideo);
    handleFiles(files);
  };

  const completeSubmit = (
    updatedOrder: MarketplaceOrder,
    intent?: PartnerPreflightIntent,
  ) => {
    if (useAuth.getState().activeTenantId !== activeTenantId) return;
    const updatedScript = updatedOrder.scripts.find(
      (candidate) => candidate.id === script.id,
    );
    const scriptReviewId = reviewIdAfterPartnerSubmit(updatedScript, intent);
    if (scriptReviewId && updatedScript) {
      onUploadSuccess({
        scriptReviewId,
        scriptTitle: title,
        clientName: order.tenant.name,
        submissionStatus: updatedScript.fulfillmentStatus,
      });
    }
    setSelectedFile(null);
    setReplaceVideo(null);
    setReplaceDialogVideo(null);
  };

  const handleRetryNotify = () => {
    if (!activeTenantId) return;
    const pendingReviewId =
      script.notifyLegs &&
      typeof script.notifyLegs === "object" &&
      (script.notifyLegs as { reviewOnlyReviewId?: string | null })
        .reviewOnlyReviewId;
    retryPreflightReview.mutate(
      {
        partnerTenantId: activeTenantId,
        orderId: order.id,
        orderScriptId: script.id,
      },
      {
        onSuccess: (updatedOrder) => {
          const updatedScript = updatedOrder.scripts.find(
            (candidate) => candidate.id === script.id,
          );
          const reviewId = pendingReviewId ?? undefined;
          const deliverableId = reviewId
            ? updatedScript?.videos?.find((video) => video.scriptReviewId === reviewId)
                ?.id
            : undefined;
          completeSubmit(
            updatedOrder,
            deliverableId ? { deliverableId } : undefined,
          );
        },
      },
    );
  };

  const handleFiles = (files: FileList | File[]) => {
    const nextFile = files[0];
    if (!nextFile) return;
    const contentType = resolvePartnerPreflightContentType(
      nextFile,
      PARTNER_VIDEO_POLICY,
    );
    const validation = validateUploadMeta(
      { contentType, sizeBytes: nextFile.size },
      PARTNER_VIDEO_POLICY,
    );
    if (!validation.ok) {
      setSelectedFile(null);
      setFileRejection(
        t("marketplace.partner.fileRejected", {
          fileName: nextFile.name,
          reason: validation.reason.replace(/_/g, " ").toLowerCase(),
          defaultValue: "{{fileName}} — {{reason}}",
        }),
      );
      return;
    }
    setSelectedFile(nextFile);
    setFileRejection(null);
  };

  const handleSubmit = async () => {
    if (!selectedFile || !activeTenantId) return;
    setReplaceDialogVideo(null);
    const controller = new AbortController();
    abortRef.current = controller;
    setUploading(true);
    setUploadProgress(0);
    setUploadPhase("preparing");
    try {
      const upload = await runPartnerPreflightUploadFlow({
        tenantId: activeTenantId,
        file: selectedFile,
        signal: controller.signal,
        onProgress: (percent) => {
          setUploadProgress(percent);
          if (percent >= 100) setUploadPhase("confirming");
        },
        onPhase: setUploadPhase,
      });
      if (controller.signal.aborted) return;
      resetUploadState();
      setPendingUploadId(upload.uploadId);
      voiceoverCheck.mutate({
        partnerTenantId: activeTenantId,
        orderId: order.id,
        orderScriptId: script.id,
        uploadId: upload.uploadId,
      });
    } catch (error) {
      if (controller.signal.aborted) return;
      if (!(error instanceof DOMException && error.name === "AbortError")) {
        toast.error(
          getUserFacingApiMessage(error) ||
            t(
              "marketplace.partner.preflightUploadError",
              "Couldn't submit preflight",
            ),
        );
      }
      resetUploadState();
    }
  };

  const closeConfirm = () => {
    setPendingUploadId(null);
    voiceoverCheck.reset();
  };

  const handleConfirmUpload = () => {
    if (!pendingUploadId || !activeTenantId) return;
    const intent = resolvePartnerPreflightIntent(script, replaceVideo);
    if (hasVideos && !intent) return;
    const uploadId = pendingUploadId;
    closeConfirm();
    setUploadPhase("submitting");
    uploadPreflight.mutate(
      {
        partnerTenantId: activeTenantId,
        orderId: order.id,
        orderScriptId: script.id,
        uploadId,
        ...(intent ? { intent } : {}),
      },
      {
        onSuccess: (updatedOrder) => completeSubmit(updatedOrder, intent),
        onSettled: resetUploadState,
      },
    );
  };

  const resetUploadState = () => {
    abortRef.current = null;
    setUploading(false);
    setUploadProgress(0);
    setUploadPhase("idle");
  };

  const retryBanner = canRetryNotify ? (
    <div
      role="region"
      aria-labelledby="partner-upload-retry-title"
      className="flex flex-col gap-3 rounded-md border border-warning/40 bg-warning/10 px-3 py-3"
    >
      <div>
        <h2
          id="partner-upload-retry-title"
          className="text-sm font-medium text-foreground"
        >
          {t(
            "marketplace.partner.upload.retryTitle",
            "Couldn't queue the review",
          )}
        </h2>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          {t(
            "marketplace.partner.upload.retryDescription",
            "The file is saved. Retry sending it for review.",
          )}
        </p>
      </div>
      <Button
        type="button"
        size="sm"
        disabled={retryPreflightReview.isPending}
        onClick={handleRetryNotify}
      >
        {retryPreflightReview.isPending
          ? t("marketplace.partner.upload.retrying", "Retrying…")
          : t("marketplace.partner.upload.retry", "Retry sending for review")}
      </Button>
    </div>
  ) : null;

  if (canRetryNotify && !hasVideos) {
    return (
      <section className="flex flex-col gap-3">{retryBanner}</section>
    );
  }

  return (
    <section
      aria-labelledby="partner-upload-title"
      className="flex flex-col gap-3"
    >
      <div>
        <h2
          id="partner-upload-title"
          className="text-sm font-medium text-foreground"
        >
          {script.fulfillmentStatus === "SENT_FOR_REVISION"
            ? t("marketplace.partner.upload.revisionTitle", "Upload revision")
            : t("marketplace.partner.upload.title", "Upload work")}
        </h2>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          {t(
            "marketplace.partner.upload.description",
            "Add the production file, then submit it for ops review.",
          )}
        </p>
      </div>
      {retryBanner}
      {hasVideos ? (
        <PartnerLineVideos
          order={order}
          script={script}
          title={title}
          abortRef={abortRef}
          onUploadSuccess={onUploadSuccess}
          onAddVideoSlot={() => {
            if (!activeTenantId) return;
            addVideoSlot.mutate({
              partnerTenantId: activeTenantId,
              orderId: order.id,
              orderScriptId: script.id,
            });
          }}
          isAddingVideoSlot={addVideoSlot.isPending}
          initialExpandedSlotKey={initialReplaceVideoKey}
        />
      ) : null}
      {!hasVideos ? (
      <>
      <UploadDropzone
        ref={uploadDropzoneRef}
        onFiles={handleFiles}
        accept={PARTNER_PREFLIGHT_UPLOAD.accept}
        multiple={false}
        disabled={isSubmitting}
        browseTrigger="button"
        className="py-5"
        titleText={t(
          "marketplace.partner.dropzoneTitle",
          "Drag, drop or paste video",
        )}
        constraintsText={t("marketplace.partner.dropzoneConstraints", {
          formats: PARTNER_PREFLIGHT_UPLOAD.formats,
          videoMax: PARTNER_PREFLIGHT_UPLOAD.videoMax,
          defaultValue: "{{formats}} · Videos up to {{videoMax}}",
        })}
        buttonLabel={
          selectedFile
            ? t("marketplace.partner.dropzoneReplace", "Replace file")
            : t("marketplace.partner.dropzoneBrowse", "Browse files")
        }
      />
      <OrderScriptThumbnails
        orderId={order.id}
        orderScriptId={script.id}
        partnerTenantId={activeTenantId}
        targetRatios={order.handoff?.targetRatios}
        thumbnails={script.thumbnails}
        editable={canChangePartnerVideos(script.fulfillmentStatus)}
      />
      {fileRejection ? (
        <p className="flex items-center gap-1.5 text-xs text-error">
          <Icon name="alert-circle" size="xs" color="inherit" aria-hidden />
          {fileRejection}
        </p>
      ) : null}
      {selectedFile && !isSubmitting ? (
        <div className="flex min-h-11 items-center justify-between gap-3 rounded-md bg-muted/40 px-3 py-2">
          <span className="min-w-0 truncate text-xs text-foreground">
            {selectedFile.name}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedFile(null);
            }}
          >
            {t("marketplace.partner.upload.remove", "Remove")}
          </Button>
        </div>
      ) : null}
      {isSubmitting && uploadPhase !== "idle" ? (
        <PartnerPreflightUploadProgress
          phase={uploadPhase}
          progress={uploadProgress}
          fileName={selectedFile?.name}
        />
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          disabled={
            !selectedFile ||
            isSubmitting ||
            (hasVideos && !canSubmitWithVideos)
          }
          onClick={() => void handleSubmit()}
        >
          {isSubmitting
            ? t("marketplace.partner.preflightUploading", "Submitting…")
            : hasVideos
              ? effectiveReplaceVideo
                ? t("marketplace.partner.videos.replaceSubmit", "Replace video")
                : t("marketplace.partner.videos.addSubmit", "Add video")
              : t(
                  "marketplace.partner.preflightUpload",
                  "Submit work for review",
                )}
        </Button>
        {isSubmitting && isPartnerUploadCancellablePhase(uploadPhase) ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              abortRef.current?.abort();
              resetUploadState();
            }}
          >
            {t("marketplace.partner.upload.cancel", "Cancel upload")}
          </Button>
        ) : null}
      </div>
      <PartnerVideoConfirmDialog
        open={pendingUploadId !== null}
        file={selectedFile}
        scriptTitle={title}
        isCheckingVoiceover={voiceoverCheck.isPending}
        voiceoverMismatch={voiceoverCheck.data ?? null}
        onConfirm={handleConfirmUpload}
        onCancel={closeConfirm}
      />
      </>
      ) : null}
    </section>
  );
}

function ScriptField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <h3 className="text-[11px] uppercase tracking-label text-muted-foreground">
        {label}
      </h3>
      <p className="mt-1 whitespace-pre-wrap text-drawer-body text-foreground">
        {value}
      </p>
    </div>
  );
}

function QueueSkeleton() {
  return (
    <div
      className="overflow-hidden rounded-md border border-border"
      aria-hidden
    >
      <Skeleton className="h-10 w-full rounded-none" />
      {Array.from({ length: 6 }, (_, index) => (
        <div
          key={index}
          className="grid grid-cols-7 gap-3 border-t border-border px-3 py-3"
        >
          <Skeleton className="h-5 w-5" />
          <Skeleton className="col-span-2 h-8 w-full" />
          <Skeleton className="h-5 w-16" />
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-5 w-16" />
          <Skeleton className="h-8 w-20" />
        </div>
      ))}
    </div>
  );
}

function QueueState({
  icon,
  title,
  body,
  action,
}: {
  icon: "alert-circle" | "file";
  title: string;
  body: string;
  action: ReactNode;
}) {
  return (
    <section className="flex min-h-64 flex-col items-center justify-center gap-3 rounded-md border border-dashed border-border px-6 py-12 text-center">
      <Icon name={icon} size="lg" color="muted" aria-hidden />
      <div>
        <h2 className="text-sm font-medium text-foreground">{title}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{body}</p>
      </div>
      {action}
    </section>
  );
}

export function isPartnerQueueInteractiveTarget(
  target: EventTarget | null,
): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return Boolean(
    target.closest(
      "button, a[href], input, select, textarea, [contenteditable='true'], [role='button'], [role='link'], [role='textbox'], [role='combobox'], [role='menuitem']",
    ),
  );
}
